import React from 'react';

interface OverlayAction {
  label: string;
  onClick: () => void;
}

interface ServiceInterruptionOverlayProps {
    secondsRemaining: number;
    totalSeconds: number;
    title: string;
    message: string;
    showProgressBar?: boolean;
    showCountdown?: boolean;
    onReturnToLogin?: () => void;
    primaryAction?: OverlayAction;
    secondaryAction?: OverlayAction;
}

const ServiceInterruptionOverlay: React.FC<ServiceInterruptionOverlayProps> = ({
    secondsRemaining,
    totalSeconds,
    title,
    message,
    showProgressBar = false,
    showCountdown = true,
    onReturnToLogin,
    primaryAction,
    secondaryAction,
}) => {
    const clampedRemaining = Math.max(secondsRemaining, 0);
    const elapsedRatio = totalSeconds > 0 ? (totalSeconds - clampedRemaining) / totalSeconds : 0;
    const progressPercent = Math.min(Math.max(elapsedRatio * 100, 0), 100);
    const signInAction = primaryAction ?? (onReturnToLogin
        ? { label: 'Sign in again', onClick: onReturnToLogin }
        : undefined);

    return (
        <div className="service-interruption-overlay" role="status" aria-live="polite">
            <div className="service-interruption-content">
                <div className="service-interruption-spinner" aria-hidden="true" />
                <h2>{title}</h2>
                <p>{message}</p>
                {showProgressBar && (
                    <div className="service-interruption-progress" aria-hidden="true">
                        <div
                            className="service-interruption-progress-fill"
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                )}
                {showCountdown && totalSeconds > 0 && (
                    <p className="service-interruption-countdown">
                        Retrying in {clampedRemaining} second{clampedRemaining === 1 ? '' : 's'}.
                    </p>
                )}
                <div className="service-interruption-actions">
                    {signInAction && (
                        <button
                            type="button"
                            className="service-interruption-login-button service-interruption-login-button--primary"
                            onClick={signInAction.onClick}
                        >
                            {signInAction.label}
                        </button>
                    )}
                    {secondaryAction && (
                        <button
                            type="button"
                            className="service-interruption-login-button"
                            onClick={secondaryAction.onClick}
                        >
                            {secondaryAction.label}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ServiceInterruptionOverlay;
